import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty, Switch, Statistic, Input,
} from 'antd';
import {
  ThunderboltOutlined, PauseCircleOutlined, PlayCircleOutlined, ClearOutlined,
  ApiOutlined, FilterOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { getEvents, getReaders, getLiveStream } from '../api';
import dayjs from 'dayjs';
import useWebSocket from '../hooks/useWebSocket';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';
const TERMINAL_BG = '#0a0e1a';
const TERMINAL_LINE = '#11172a';
const TERMINAL_HOVER = '#1a2138';

// Reader colors (terminal-style palette)
const READER_COLORS = ['#00ffaa', '#00d4ff', '#ffae00', '#ff5e5e', '#c084fc', '#ff80c8', '#84cc16', '#fbbf24'];
const colorForReader = (name) => {
  if (!name) return '#94a3b8';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return READER_COLORS[Math.abs(hash) % READER_COLORS.length];
};

const rssiTone = (v) => {
  if (v == null) return '#94a3b8';
  if (v >= -55) return '#10b981';
  if (v >= -65) return '#22d3ee';
  if (v >= -75) return '#f59e0b';
  return '#ef4444';
};

export default function LiveReadStream() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [readers, setReaders] = useState([]);
  const [filterReader, setFilterReader] = useState(null);
  const [filterTag, setFilterTag] = useState('');
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [stream, setStream] = useState([]);
  const [stats, setStats] = useState({ total: 0, tps: 0, peak: 0 });

  const lastIdRef = useRef(0);
  const consoleRef = useRef(null);
  const recentRef = useRef([]); // last 60 seconds of timestamps for TPS calc
  const intervalRef = useRef(null);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!eventId) return;
    getReaders({ event_id: eventId }).then(r => setReaders(r.data.data || [])).catch(() => {});
    setStream([]); lastIdRef.current = 0;
  }, [eventId]);

  // Poll for new reads every 1.5s
  const poll = useCallback(async () => {
    if (!eventId || paused) return;
    try {
      const r = await getLiveStream({
        event_id: eventId,
        reader_id: filterReader || undefined,
        since_id: lastIdRef.current || undefined,
        limit: 100,
      });
      const newRows = (r.data.data || []).reverse(); // oldest first
      if (newRows.length === 0) return;
      lastIdRef.current = Math.max(lastIdRef.current, ...newRows.map(x => x.id));

      // Update TPS rolling buffer
      const now = Date.now();
      newRows.forEach(() => recentRef.current.push(now));
      // Trim to last 60s
      recentRef.current = recentRef.current.filter(t => now - t < 60000);
      const tps = +(recentRef.current.length / 60).toFixed(1);

      setStream(prev => {
        const merged = [...prev, ...newRows];
        // Keep last 1000 lines max
        return merged.length > 1000 ? merged.slice(merged.length - 1000) : merged;
      });
      setStats(s => ({
        total: s.total + newRows.length,
        tps,
        peak: Math.max(s.peak, tps),
      }));
    } catch {}
  }, [eventId, paused, filterReader]);

  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, 1500);
    return () => clearInterval(intervalRef.current);
  }, [poll]);

  // WebSocket trigger for instant poll on new scan
  useWebSocket(useCallback(() => { if (!paused) poll(); }, [paused, poll]));

  // Auto-scroll
  useEffect(() => {
    if (!autoScroll || !consoleRef.current) return;
    consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [stream, autoScroll]);

  // Filter applied client-side
  const filtered = filterTag
    ? stream.filter(s => (s.rfid_tag || '').toLowerCase().includes(filterTag.toLowerCase()) ||
                         (s.delegate_name || '').toLowerCase().includes(filterTag.toLowerCase()))
    : stream;

  const exportCsv = () => {
    const header = 'id,timestamp,rfid_tag,delegate_name,reader_name,location_name,rssi,antenna_port,direction,scan_source\n';
    const rows = filtered.map(s => [
      s.id, s.scanned_at, s.rfid_tag, s.delegate_name || '',
      s.reader_name || '', s.location_name || '', s.rssi || '',
      s.antenna_port || '', s.reader_direction || '', s.scan_source || ''
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rfid_stream_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <ThunderboltOutlined style={{ marginRight: 8, color: '#00ffaa' }} />Live Read Stream
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Real-time RFID read console · every tag read appears as it happens
          </Text>
        </Col>
        <Col>
          <Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 200 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Select value={filterReader} onChange={setFilterReader} placeholder="All readers" allowClear style={{ width: 200 }}>
              {readers.map(r => <Option key={r.id} value={r.id}><ApiOutlined /> {r.reader_name}</Option>)}
            </Select>
          </Space>
        </Col>
      </Row>

      {/* Live KPIs */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={8} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Reads in Stream" value={stats.total.toLocaleString()} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Live TPS" value={stats.tps} suffix="t/s"
              prefix={<ThunderboltOutlined />} valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #f59e0b' }}>
            <Statistic title="Peak TPS" value={stats.peak} suffix="t/s" valueStyle={{ color: '#f59e0b' }} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #10b981' }}>
            <Space wrap size={6}>
              <Badge status={paused ? 'warning' : 'processing'} text={paused ? 'PAUSED' : 'LIVE'} />
              <Button size="small" icon={paused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                onClick={() => setPaused(!paused)} type={paused ? 'primary' : 'default'}>
                {paused ? 'Resume' : 'Pause'}
              </Button>
              <Button size="small" icon={<ClearOutlined />} onClick={() => { setStream([]); setStats({ total: 0, tps: 0, peak: 0 }); }}>Clear</Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Tag/name filter + auto-scroll */}
      <Card size="small" style={{ marginBottom: 12, borderRadius: 10 }}>
        <Row gutter={12} align="middle">
          <Col flex="auto">
            <Input
              prefix={<FilterOutlined />}
              placeholder="Filter by RFID tag or delegate name…"
              value={filterTag}
              onChange={e => setFilterTag(e.target.value)}
              allowClear
            />
          </Col>
          <Col>
            <Space>
              <Text style={{ fontSize: 12 }}>Auto-scroll</Text>
              <Switch checked={autoScroll} onChange={setAutoScroll} size="small" />
              <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={filtered.length === 0}>
                Export CSV
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Terminal-style console */}
      <Card
        bodyStyle={{ padding: 0, background: TERMINAL_BG }}
        style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #1e293b' }}
      >
        <div style={{
          background: '#1e293b', padding: '8px 14px', display: 'flex', justifyContent: 'space-between',
          fontFamily: 'Menlo, Consolas, monospace', fontSize: 11, color: '#94a3b8',
        }}>
          <Space>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }}/>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }}/>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981', display: 'inline-block' }}/>
            <Text style={{ color: '#94a3b8', marginLeft: 12 }}>rfid-stream — {filtered.length} reads in buffer</Text>
          </Space>
          <Text style={{ color: '#94a3b8' }}>
            {paused ? '⏸ paused' : '● live'} · {dayjs().format('HH:mm:ss')}
          </Text>
        </div>

        <div ref={consoleRef} style={{
          height: 540, overflowY: 'auto', padding: 8, fontFamily: 'Menlo, Consolas, monospace', fontSize: 12,
          color: '#e2e8f0',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
              <Empty description={<Text style={{ color: '#64748b' }}>Waiting for reads…</Text>}
                image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            filtered.map(s => {
              const c = colorForReader(s.reader_name);
              return (
                <div key={s.id} style={{
                  padding: '3px 8px', display: 'flex', gap: 12, alignItems: 'center',
                  borderBottom: '1px solid #11172a',
                }}
                onMouseEnter={e => e.currentTarget.style.background = TERMINAL_HOVER}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ color: '#64748b', minWidth: 78, fontSize: 11 }}>
                    {dayjs(s.scanned_at).format('HH:mm:ss.SSS')}
                  </span>
                  <span style={{ color: c, minWidth: 130, fontWeight: 700 }}>
                    [{s.reader_name || 'unknown'}]
                  </span>
                  <span style={{ color: '#a78bfa', minWidth: 220 }}>
                    🏷 {s.rfid_tag}
                  </span>
                  <span style={{ color: rssiTone(s.rssi), minWidth: 68, textAlign: 'right' }}>
                    {s.rssi != null ? `${s.rssi}dBm` : '—'}
                  </span>
                  <span style={{ color: '#fbbf24', minWidth: 26, textAlign: 'center' }}>
                    A{s.antenna_port || 1}
                  </span>
                  <span style={{ color: s.delegate_name ? '#10b981' : '#64748b', flex: 1 }}>
                    {s.delegate_name ? `→ ${s.delegate_name}` : '→ <unregistered>'}
                  </span>
                  {s.location_name && (
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>📍 {s.location_name}</span>
                  )}
                  {s.reader_direction && (
                    <Tag color={s.reader_direction === 'Entry' ? 'green' : 'orange'} style={{ margin: 0, fontSize: 10 }}>
                      {s.reader_direction === 'Entry' ? '↑' : '↓'} {s.reader_direction}
                    </Tag>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
}
